import { API_BASE_URL } from './api';
import { authService } from './authService';
import { DivePoint, DiveStats } from '../types/Dive';

export const PostType = {
  DiveLog: 'DiveLog',
  Text: 'Text',
  EquipmentAdded: 'EquipmentAdded',
  CheckIn: 'CheckIn',
  ConditionsReport: 'ConditionsReport',
  Story: 'Story',
} as const;

export const PostVisibility = {
  Public: 'Public',
  Buddies: 'Buddies',
  Followers: 'Followers',
  Private: 'Private',
  Group: 'Group',
  Club: 'Club',
} as const;
export type PostVisibilityValue = typeof PostVisibility[keyof typeof PostVisibility];

export interface PublicPostHighlight {
  postId: string;
  authorDisplayName: string;
  authorProfilePhotoUrl?: string | null;
  type: string;
  text?: string | null;
  diveLabel?: string | null;
  equipmentLabel?: string | null;
  diveSiteName?: string | null;
  reactionCount: number;
  commentCount: number;
  createdAt: string;
}

export const ReactionType = {
  Ok: 'Ok',
  Shaka: 'Shaka',
  Heart: 'Heart',
} as const;
export type ReactionTypeValue = typeof ReactionType[keyof typeof ReactionType];

export interface EquipmentSnapshot {
  equipmentType: string;
  brand?: string | null;
  model?: string | null;
}

export interface DiveSnapshot {
  startTime: string;
  endTime: string;
  duration: string;
  maxDepth: number;
  avgDepth: number;
  waterTemperature: number;
  diveType: string;
  location: string;
  latitude?: number;
  longitude?: number;
  diveProfile: DivePoint[];
  stats: DiveStats;
}

export interface Post {
  postId: string;
  authorUserId: string;
  authorDisplayName: string;
  authorProfilePhotoUrl?: string | null;
  type: string;
  visibility: string;
  text?: string | null;
  photoKeys: string[];
  linkedDiveId?: string | null;
  diveSnapshot?: DiveSnapshot | null;
  diveLabel?: string | null;
  linkedEquipmentId?: string | null;
  equipmentSnapshot?: EquipmentSnapshot | null;
  equipmentLabel?: string | null;
  groupId?: string | null;
  clubId?: string | null;
  /** Only meaningful for a Group/Club post — at most one pinned post per group/club. */
  /** Set only on a ConditionsReport. Always metric — convert for display with the viewer's unit preferences. */
  conditions?: SiteConditions | null;
  /** Set on a CheckIn (and on a Public post tagged to a site hub). */
  diveSiteSlug?: string | null;
  diveSiteName?: string | null;
  /** Set only on a Story: when it stops being served. */
  expiresAt?: string | null;
  isPinned: boolean;
  /** The author showcased this on their own profile (max 3). */
  isFeatured?: boolean;
  taggedUserIds: string[];
  taggedGroupIds: string[];
  taggedClubIds: string[];
  /** People tagged by name who weren't on the app yet; `claimedByUserId` is set once they joined through the claim link. */
  pendingTags?: PendingTag[];
  createdAt: string;
  editedAt?: string | null;
  viewerHasReacted: boolean;
  /** The caller's own stacked reaction types — a user may have more than one on the same post. */
  viewerReactionTypes: ReactionTypeValue[];
  commentCount: number;
  /** Total reaction instances — a user with 2 stacked types counts twice. Use reactorCount for "how many people reacted". */
  reactionCount: number;
  reactionCounts: Partial<Record<ReactionTypeValue, number>>;
  /** Unique people who left at least one reaction. Always <= reactionCount. */
  reactorCount: number;
  /** Set when this post is a repost — a thin wrapper; reactions/comments stay on the original. */
  repostedFromPostId?: string | null;
  repostedFromAuthorUserId?: string | null;
  repostedFromAuthorDisplayName?: string | null;
  /** How many times this post has been reposted. Only meaningful on an original post. */
  repostCount: number;
}

export type WaterCurrent = 'None' | 'Light' | 'Moderate' | 'Strong';

export interface SiteConditions {
  visibilityMeters?: number | null;
  waterTemperatureC?: number | null;
  current?: WaterCurrent | null;
  /** 1 (poor) – 5 (excellent). */
  rating?: number | null;
}

export interface CreateConditionsReportRequest extends SiteConditions {
  diveSiteSlug: string;
  diveSiteName: string;
  note?: string;
}

/** What the last couple of weeks of Public reports add up to. Null measures mean nobody reported them. */
export interface SiteConditionsSummary {
  reportCount: number;
  windowDays: number;
  medianVisibilityMeters?: number | null;
  medianWaterTemperatureC?: number | null;
  mostCommonCurrent?: WaterCurrent | null;
  averageRating?: number | null;
  latestReportAt?: string | null;
}

export interface PendingTag {
  tagId: string;
  name: string;
  claimedByUserId?: string | null;
}

export interface CreateTextPostRequest {
  text?: string;
  /** Ties a Public post to a dive site's community hub. */
  diveSiteSlug?: string;
  photoKeys?: string[];
  visibility?: string;
  taggedUserIds?: string[];
  taggedGroupIds?: string[];
  taggedClubIds?: string[];
  /** Names of people not on the app yet to tag; the author gets a claim link for each. */
  pendingTagNames?: string[];
}

export interface CreateDiveLogPostRequest {
  text?: string;
  photoKeys?: string[];
  visibility?: string;
  taggedUserIds?: string[];
  taggedGroupIds?: string[];
  taggedClubIds?: string[];
  /** Names of people not on the app yet to tag; the author gets a claim link for each. */
  pendingTagNames?: string[];
}

export interface CreateEquipmentPostRequest {
  equipmentId: string;
  text?: string;
  visibility?: string;
}

export interface CreateGroupTextPostRequest {
  text?: string;
  photoKeys?: string[];
}

export interface CreateClubTextPostRequest {
  text?: string;
  photoKeys?: string[];
  taggedUserIds?: string[];
  taggedGroupIds?: string[];
  taggedClubIds?: string[];
}

export interface Reaction {
  postId: string;
  userId: string;
  /** Stacked reaction types this user left on the post — may hold more than one. */
  types: ReactionTypeValue[];
  createdAt: string;
}

export interface Comment {
  commentId: string;
  postId: string;
  /** Null for a top-level comment; otherwise the top-level comment this replies to. Nesting is single-level. */
  parentCommentId?: string | null;
  authorUserId: string;
  authorDisplayName: string;
  authorProfilePhotoUrl?: string | null;
  text: string;
  taggedUserIds: string[];
  taggedGroupIds: string[];
  taggedClubIds: string[];
  createdAt: string;
}

const authJsonHeaders = () => ({ 'Content-Type': 'application/json', ...authService.getAuthHeaders() });

// The post-photos bucket allows public GET under posts/* (see terraform/s3_post_photos.tf),
// so a photoKey can be rendered directly from its bucket URL — no backend round-trip needed.
export const photoUrl = (photoKey: string): string =>
  `https://aidivecoach-post-photos.s3.us-east-2.amazonaws.com/${photoKey}`;

export const postService = {
  getFeed: async (): Promise<Post[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/feed`, { headers: { ...authService.getAuthHeaders() } });
    if (!res.ok) throw new Error('Failed to load feed');
    return res.json();
  },

  // Most-reacted posts from the last 7 days that the caller can actually see — never a
  // truly global ranking, since visibility (Private/Buddies/Followers/Group/Club) still applies.
  getTrendingPosts: async (): Promise<Post[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/trending`, { headers: { ...authService.getAuthHeaders() } });
    if (!res.ok) throw new Error('Failed to load trending posts');
    return res.json();
  },

  // Anonymous: a trimmed preview of the most-reacted Public posts, for the signed-out landing page.
  getPublicHighlights: async (): Promise<PublicPostHighlight[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/public-highlights`);
    if (!res.ok) throw new Error('Failed to load community highlights');
    return res.json();
  },

  // Recent Public posts from a set of divers — the feed of a regional community page.
  // Pass a region slug and the server picks the authors from the region index; an explicit author list still works.
  getCommunityFeed: async (source: string[] | { regionSlug: string } | { countrySlug: string }): Promise<Post[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/community-feed`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(Array.isArray(source) ? { authorUserIds: source } : source),
    });
    if (!res.ok) throw new Error('Failed to load community feed');
    return res.json();
  },

  // A dive site hub's community content: Public posts tagged to the site, plus Public dive logs done nearby.
  getSiteFeed: async (slug: string, latitude: number, longitude: number, radiusKm: number): Promise<Post[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/site-feed`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ slug, latitude, longitude, radiusKm }),
    });
    if (!res.ok) throw new Error('Failed to load site community');
    return res.json();
  },

  createCheckIn: async (request: { diveSiteSlug: string; diveSiteName: string; note?: string; visibility: string }): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/check-in`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error((await res.text()) || 'Failed to check in');
    return res.json();
  },

  createConditionsReport: async (request: CreateConditionsReportRequest): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/conditions`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error((await res.text()) || 'Failed to post your report');
    return res.json();
  },

  getSiteConditions: async (slug: string): Promise<SiteConditionsSummary> => {
    const res = await fetch(`${API_BASE_URL}/posts/site-conditions`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ slug }),
    });
    if (!res.ok) throw new Error('Failed to load conditions');
    return res.json();
  },

  getPostsByAuthor: async (authorUserId: string): Promise<Post[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/author/${authorUserId}`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load posts');
    return res.json();
  },

  createTextPost: async (request: CreateTextPostRequest): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/text`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to create post');
    }
    return res.json();
  },

  createDiveLogPost: async (diveId: string, request: CreateDiveLogPostRequest): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/dive/${diveId}`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to create post');
    }
    return res.json();
  },

  createEquipmentPost: async (request: CreateEquipmentPostRequest): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/equipment`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to create post');
    }
    return res.json();
  },

  // Visibility is a preference, not a guarantee — the server caps it at the original post's
  // own visibility so a repost can never widen its audience.
  repostPost: async (postId: string, visibility: PostVisibilityValue = PostVisibility.Buddies): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/repost`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ visibility }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to repost');
    }
    return res.json();
  },

  // Group/Club admin or owner only. Pinning a post unpins whatever was previously pinned
  // in that same group/club — at most one pinned post at a time.
  pinPost: async (postId: string): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/pin`, {
      method: 'POST',
      headers: authJsonHeaders(),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to pin post');
    }
    return res.json();
  },

  // Author only. Showcases a post at the top of the author's own profile; capped at 3.
  featurePost: async (postId: string): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/feature`, { method: 'POST', headers: authJsonHeaders() });
    if (!res.ok) throw new Error((await res.text()) || 'Failed to feature post');
    return res.json();
  },

  unfeaturePost: async (postId: string): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/unfeature`, { method: 'POST', headers: authJsonHeaders() });
    if (!res.ok) throw new Error((await res.text()) || 'Failed to unfeature post');
    return res.json();
  },

  unpinPost: async (postId: string): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/unpin`, {
      method: 'POST',
      headers: authJsonHeaders(),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to unpin post');
    }
    return res.json();
  },

  createGroupPost: async (groupId: string, request: CreateGroupTextPostRequest): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/group/${groupId}`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to create post');
    }
    return res.json();
  },

  getGroupFeed: async (groupId: string): Promise<Post[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/group/${groupId}/feed`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load group feed');
    return res.json();
  },

  createClubPost: async (clubId: string, request: CreateClubTextPostRequest): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/club/${clubId}`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to create post');
    }
    return res.json();
  },

  getClubFeed: async (clubId: string): Promise<Post[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/club/${clubId}/feed`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load club feed');
    return res.json();
  },

  getPost: async (postId: string): Promise<Post | null> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (res.status === 404 || res.status === 403) return null;
    if (!res.ok) throw new Error('Failed to load post');
    return res.json();
  },

  deletePost: async (postId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to delete post');
  },

  getPhotoUploadUrl: async (contentType: string): Promise<{ uploadUrl: string; photoKey: string }> => {
    const res = await fetch(`${API_BASE_URL}/posts/photo-upload-url`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ contentType }),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || 'Failed to get upload URL');
    }
    return res.json();
  },

  // Photos and short videos share one presigned-PUT flow; the server picks the key's extension from the content type.
  // onProgress gets 0–1 as bytes leave the browser. fetch can't report upload progress, so this one
  // PUT uses XMLHttpRequest.
  uploadPhoto: async (file: File, onProgress?: (fraction: number) => void): Promise<string> => {
    const { uploadUrl, photoKey } = await postService.getPhotoUploadUrl(file.type);
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) { onProgress?.(1); resolve(); }
        else reject(new Error('Failed to upload photo'));
      };
      xhr.onerror = () => reject(new Error('Failed to upload photo'));
      xhr.onabort = () => reject(new Error('Upload cancelled'));
      xhr.send(file);
    });
    return photoKey;
  },

  addReaction: async (postId: string, type: ReactionTypeValue): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/reactions`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ type }),
    });
    if (!res.ok) throw new Error('Failed to react to post');
    return res.json();
  },

  removeReaction: async (postId: string): Promise<Post> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/reactions`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to remove reaction');
    return res.json();
  },

  getReactions: async (postId: string): Promise<Reaction[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/reactions`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load reactions');
    return res.json();
  },

  getComments: async (postId: string): Promise<Comment[]> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/comments`, {
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to load comments');
    return res.json();
  },

  addComment: async (postId: string, text: string, taggedUserIds?: string[], taggedGroupIds?: string[], taggedClubIds?: string[], parentCommentId?: string | null): Promise<Comment> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/comments`, {
      method: 'POST',
      headers: authJsonHeaders(),
      body: JSON.stringify({ text, taggedUserIds, taggedGroupIds, taggedClubIds, parentCommentId }),
    });
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(errText || 'Failed to add comment');
    }
    return res.json();
  },

  deleteComment: async (postId: string, commentId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/posts/${postId}/comments/${commentId}`, {
      method: 'DELETE',
      headers: { ...authService.getAuthHeaders() },
    });
    if (!res.ok) throw new Error('Failed to delete comment');
  },
};
